import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNotification } from "../../contexts/NotificationContext";
import { apiService } from "../../backend/api";
import { getBaseUrl } from "../../config/apiConfig";
import { Plus, Trash2, Tag, Percent, Euro, Info, Calendar, Users, ShieldCheck, CreditCard, Rocket, Gift, Download, Save, Loader2, ToggleLeft, ToggleRight, Clock, AlertCircle } from "lucide-react";
import AdminLaunchPromotions from "./AdminLaunchPromotions";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card } from "../../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";

const AdminDiscounts = () => {
  const { t } = useTranslation();
  const { showNotification } = useNotification();
  const [coupons, setCoupons] = useState([]);
  const [promoCodes, setPromoCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    id: "",
    percent_off: "",
    amount_off: "",
    duration: "once",
    duration_in_months: "",
    max_redemptions: "",
    redeem_by: "",
    first_time_transaction: false,
    minimum_amount: "",
    max_redemptions_promo: "",
  });

  const resetForm = () => {
    setFormData({
      name: "", id: "", percent_off: "", amount_off: "",
      duration: "once", duration_in_months: "", max_redemptions: "",
      redeem_by: "", first_time_transaction: false, minimum_amount: "",
      max_redemptions_promo: "",
    });
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const [couponsData, promoData] = await Promise.all([
        apiService.request(`${getBaseUrl()}/api/admin/coupons`),
        apiService.request(`${getBaseUrl()}/api/admin/promo-codes`)
      ]);
      setCoupons(couponsData);
      setPromoCodes(promoData);
    } catch (err) {
      console.error("Fehler beim Laden der Discounts:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSubmit = async () => {
    try {
      await apiService.request(`${getBaseUrl()}/api/admin/coupons`, {
        method: "POST",
        body: JSON.stringify({
          ...formData,
          percent_off: formData.percent_off ? parseFloat(formData.percent_off) : undefined,
          amount_off: formData.amount_off ? parseFloat(formData.amount_off) : undefined,
          duration_in_months: formData.duration === 'repeating' ? parseInt(formData.duration_in_months) : undefined,
          max_redemptions: formData.max_redemptions ? parseInt(formData.max_redemptions) : undefined,
          redeem_by: formData.redeem_by || undefined,
          first_time_transaction: formData.first_time_transaction || false,
          minimum_amount: formData.minimum_amount ? parseFloat(formData.minimum_amount) : undefined,
          max_redemptions_promo: formData.max_redemptions_promo ? parseInt(formData.max_redemptions_promo) : undefined,
        })
      });
      setIsModalOpen(false);
      resetForm();
      fetchData();
      showNotification("Rabattcode erfolgreich erstellt", "success");
    } catch (err) {
      showNotification("Fehler beim Erstellen: " + err.message, "error");
    }
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;
    try {
      await apiService.request(`${getBaseUrl()}/api/admin/coupons/${itemToDelete}`, {
        method: "DELETE"
      });
      setDeleteConfirmOpen(false);
      setItemToDelete(null);
      fetchData();
    } catch (err) {
      showNotification("Fehler beim Löschen: " + err.message, "error");
    }
  };

  const openDeleteConfirm = (id) => {
    const cleanId = typeof id === 'string' ? id.replace(/[:\s]+$/, '') : id;
    setItemToDelete(cleanId);
    setDeleteConfirmOpen(true);
  };

  // Helper: format expiry date
  const formatExpiry = (timestamp) => {
    if (!timestamp) return null;
    return new Date(timestamp * 1000).toLocaleDateString('de-DE');
  };

  return (
    <div className="space-y-8">
      {/* Launch-Aktionen Section */}
      <AdminLaunchPromotions />

      {/* Divider */}
      <div className="border-t border-slate-200 dark:border-slate-700" />

      {/* Stripe Promo Codes Section */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-slate-800 dark:text-white">Rabatt- & Gutscheincodes</h2>
          <p className="text-slate-500 dark:text-slate-400">Manuelle Stripe-Rabattcodes (Kunde gibt Code beim Checkout ein)</p>
        </div>
        <Button onClick={() => { resetForm(); setIsModalOpen(true); }} className="rounded-xl">
          <Plus className="mr-2" size={18} />
          Neuer Rabattcode
        </Button>
      </div>

      {/* Promo Codes Table */}
      <Card className="rounded-[2rem] border-border shadow-sm overflow-hidden bg-white dark:bg-slate-800">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50 dark:bg-slate-700">
              <TableHead className="px-6 py-4">Code / Name</TableHead>
              <TableHead className="px-6 py-4">Rabatt</TableHead>
              <TableHead className="px-6 py-4">Dauer</TableHead>
              <TableHead className="px-6 py-4">Einschränkungen</TableHead>
              <TableHead className="px-6 py-4 text-center">Einlösungen</TableHead>
              <TableHead className="px-6 py-4 text-right">Aktionen</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {promoCodes.map((promo) => {
              const coupon = promo.coupon;
              const couponId = typeof coupon === 'string' ? coupon : coupon?.id;
              const couponName = typeof coupon === 'string' ? '' : coupon?.name;

              return (
                <TableRow key={promo.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-700/50 transition-colors">
                  <TableCell className="px-6 py-4">
                    <div className="flex flex-col">
                      <span className="font-bold text-slate-800 dark:text-white text-lg flex items-center">
                        <Tag size={14} className="mr-2 text-primary" />
                        {promo.code}
                      </span>
                      <span className="text-xs text-slate-500 dark:text-slate-400">{couponName || couponId}</span>
                    </div>
                  </TableCell>
                  <TableCell className="px-6 py-4 font-bold text-[#0f7699] dark:text-[#4fc3f7]">
                    {coupon?.percent_off ? (
                      <span className="flex items-center">
                        <Percent size={14} className="mr-1" />
                        {coupon.percent_off}%
                      </span>
                    ) : (
                      <span>€{(coupon?.amount_off / 100).toFixed(2)}</span>
                    )}
                  </TableCell>
                  <TableCell className="px-6 py-4">
                    <Badge variant="outline" className="text-[10px] font-bold uppercase">
                      {coupon?.duration === 'once' ? 'Einmalig' :
                       coupon?.duration === 'forever' ? 'Dauerhaft' :
                       `${coupon?.duration_in_months} Monate`}
                    </Badge>
                    {coupon?.redeem_by && (
                      <div className="text-[10px] text-slate-400 mt-1 flex items-center gap-1">
                        <Calendar size={10} />
                        bis {formatExpiry(coupon.redeem_by)}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="px-6 py-4">
                    <div className="flex flex-wrap gap-1">
                      {promo.restrictions?.first_time_transaction && (
                        <Badge className="bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400 border-blue-100 dark:border-blue-800/30 text-[9px]">Nur Neukunden</Badge>
                      )}
                      {promo.restrictions?.minimum_amount && (
                        <Badge className="bg-amber-50 text-amber-600 dark:bg-amber-900/20 dark:text-amber-400 border-amber-100 dark:border-amber-800/30 text-[9px]">Min. €{(promo.restrictions.minimum_amount / 100).toFixed(0)}</Badge>
                      )}
                      {promo.max_redemptions && (
                        <Badge className="bg-slate-50 text-slate-600 dark:bg-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-600 text-[9px]">Max. {promo.max_redemptions}x</Badge>
                      )}
                      {!promo.restrictions?.first_time_transaction && !promo.restrictions?.minimum_amount && !promo.max_redemptions && (
                        <span className="text-xs text-slate-400">–</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="px-6 py-4 text-center">
                    <div className="flex flex-col items-center">
                      <span className="font-bold text-slate-700 dark:text-white">{promo.times_redeemed}</span>
                      {coupon?.max_redemptions && (
                        <span className="text-[10px] text-slate-400">/ {coupon.max_redemptions}</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="px-6 py-4 text-right">
                    <Button variant="ghost" size="icon" onClick={() => openDeleteConfirm(couponId)} className="h-9 w-9 rounded-lg text-slate-400 hover:text-destructive">
                      <Trash2 size={18} />
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
            {promoCodes.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={6} className="py-20 text-center text-slate-400 italic">
                  Keine aktiven Rabattcodes vorhanden.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Create Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-lg rounded-[2rem] max-h-[90vh] flex flex-col">
          <DialogHeader className="shrink-0">
            <DialogTitle>Neuen Rabattcode erstellen</DialogTitle>
          </DialogHeader>
          <div className="space-y-5 py-4 overflow-y-auto flex-1 min-h-0 px-1">

            {/* Code & Name */}
            <div className="space-y-4">
              <h4 className="text-[10px] font-bold uppercase tracking-wider text-[#0f7699] dark:text-[#4fc3f7] flex items-center gap-1.5">
                <Tag size={12} /> Grundeinstellungen
              </h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-600 dark:text-slate-300">Code</label>
                  <Input
                    value={formData.id}
                    onChange={(e) => setFormData({ ...formData, id: e.target.value.toUpperCase().replace(/\s/g, '') })}
                    placeholder="z.B. NEUKUNDE20"
                  />
                  <p className="text-[10px] text-slate-400">Das was der Kunde eingibt</p>
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-600 dark:text-slate-300">Anzeigename</label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="z.B. Neukunden-Rabatt"
                  />
                  <p className="text-[10px] text-slate-400">Interner Name zur Identifikation</p>
                </div>
              </div>
            </div>

            {/* Discount Type */}
            <div className="space-y-4">
              <h4 className="text-[10px] font-bold uppercase tracking-wider text-[#0f7699] dark:text-[#4fc3f7] flex items-center gap-1.5">
                <Percent size={12} /> Rabatt
              </h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-600 dark:text-slate-300">Prozentual (%)</label>
                  <Input
                    type="number"
                    min="1"
                    max="100"
                    placeholder="z.B. 20"
                    value={formData.percent_off}
                    onChange={(e) => setFormData({ ...formData, percent_off: e.target.value, amount_off: "" })}
                  />
                  <p className="text-[10px] text-slate-400">Prozentualer Rabatt auf den Gesamtpreis</p>
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-600 dark:text-slate-300">Festbetrag (€)</label>
                  <Input
                    type="number"
                    min="0.01"
                    step="0.01"
                    placeholder="z.B. 10.00"
                    value={formData.amount_off}
                    onChange={(e) => setFormData({ ...formData, amount_off: e.target.value, percent_off: "" })}
                  />
                  <p className="text-[10px] text-slate-400">Fester Euro-Betrag wird abgezogen</p>
                </div>
              </div>
              {formData.percent_off && formData.amount_off && (
                <p className="text-[10px] text-amber-600 flex items-center gap-1"><Info size={10} /> Nur eine Rabattart gleichzeitig möglich</p>
              )}
            </div>

            {/* Duration */}
            <div className="space-y-4">
              <h4 className="text-[10px] font-bold uppercase tracking-wider text-[#0f7699] dark:text-[#4fc3f7] flex items-center gap-1.5">
                <Calendar size={12} /> Gültigkeit
              </h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-600 dark:text-slate-300">Dauer</label>
                  <select
                    className="w-full h-10 px-3 bg-white dark:bg-slate-700 border dark:border-slate-600 rounded-md text-sm text-slate-800 dark:text-white"
                    value={formData.duration}
                    onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                  >
                    <option value="once">Einmalig</option>
                    <option value="forever">Dauerhaft</option>
                    <option value="repeating">Begrenzte Monate</option>
                  </select>
                  <p className="text-[10px] text-slate-400">
                    {formData.duration === 'once' && 'Rabatt gilt nur für die erste Zahlung'}
                    {formData.duration === 'forever' && 'Rabatt gilt für alle zukünftigen Zahlungen'}
                    {formData.duration === 'repeating' && 'Rabatt gilt für die angegebene Anzahl Monate'}
                  </p>
                </div>
                {formData.duration === 'repeating' && (
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-slate-600 dark:text-slate-300">Anzahl Monate</label>
                    <Input
                      type="number"
                      min="1"
                      value={formData.duration_in_months}
                      onChange={(e) => setFormData({ ...formData, duration_in_months: e.target.value })}
                      placeholder="z.B. 3"
                    />
                    <p className="text-[10px] text-slate-400">Danach gilt der reguläre Preis</p>
                  </div>
                )}
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-600 dark:text-slate-300">Ablaufdatum</label>
                <Input
                  type="date"
                  value={formData.redeem_by}
                  onChange={(e) => setFormData({ ...formData, redeem_by: e.target.value })}
                />
                <p className="text-[10px] text-slate-400">Nach diesem Datum kann der Code nicht mehr eingelöst werden (leer = unbegrenzt)</p>
              </div>
            </div>

            {/* Restrictions */}
            <div className="space-y-4">
              <h4 className="text-[10px] font-bold uppercase tracking-wider text-[#0f7699] dark:text-[#4fc3f7] flex items-center gap-1.5">
                <ShieldCheck size={12} /> Einschränkungen
              </h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-600 dark:text-slate-300">Max. Einlösungen (gesamt)</label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.max_redemptions}
                    onChange={(e) => setFormData({ ...formData, max_redemptions: e.target.value })}
                    placeholder="Unbegrenzt"
                  />
                  <p className="text-[10px] text-slate-400">Wie oft der Code insgesamt eingelöst werden kann</p>
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-600 dark:text-slate-300">Max. pro Promo-Code</label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.max_redemptions_promo}
                    onChange={(e) => setFormData({ ...formData, max_redemptions_promo: e.target.value })}
                    placeholder="Unbegrenzt"
                  />
                  <p className="text-[10px] text-slate-400">Begrenzt Einlösungen auf Promo-Code-Ebene</p>
                </div>
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-600 dark:text-slate-300">Mindestbestellwert (€)</label>
                <Input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={formData.minimum_amount}
                  onChange={(e) => setFormData({ ...formData, minimum_amount: e.target.value })}
                  placeholder="Kein Minimum"
                />
                <p className="text-[10px] text-slate-400">Der Code ist nur gültig, wenn der Warenkorbwert diesen Betrag erreicht</p>
              </div>
              <div className="flex items-start gap-3 p-3 bg-blue-50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-800/30 rounded-xl">
                <input
                  type="checkbox"
                  id="firstTimeOnly"
                  checked={formData.first_time_transaction}
                  onChange={(e) => setFormData({ ...formData, first_time_transaction: e.target.checked })}
                  className="mt-0.5"
                />
                <label htmlFor="firstTimeOnly" className="cursor-pointer">
                  <span className="text-xs font-bold text-blue-700 dark:text-blue-400 block">Nur für Erstkäufer</span>
                  <span className="text-[10px] text-blue-600/70 dark:text-blue-400/70">Code kann nur von Kunden eingelöst werden, die noch nie bei Ihnen bezahlt haben</span>
                </label>
              </div>
            </div>

          </div>
          <DialogFooter className="shrink-0">
            <Button variant="ghost" onClick={() => setIsModalOpen(false)}>Abbrechen</Button>
            <Button onClick={handleSubmit} disabled={!formData.id || !formData.name || (!formData.percent_off && !formData.amount_off)}>
              Gutschein erstellen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="max-w-md rounded-[2rem]">
          <DialogHeader>
            <DialogTitle className="text-destructive">Rabatt löschen</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-600 dark:text-slate-300">Möchten Sie diesen Rabatt wirklich unwiderruflich löschen? Diese Aktion kann nicht rückgängig gemacht werden.</p>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="ghost" onClick={() => setDeleteConfirmOpen(false)} className="rounded-xl">
              Abbrechen
            </Button>
            <Button variant="destructive" onClick={handleDelete} className="rounded-xl">
              Endgültig löschen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDiscounts;
